import { Component, computed, inject, input, linkedSignal, signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { forkJoin, of, switchMap, catchError, map } from "rxjs";
import { ProjectService } from "../../../shared/services/project.service";
import { ShotOverlayDto } from "../../../models/shotOverlayDto";
import { ImageLoaderService } from "../../../shared/services/image-loader.service";
import { CachedProjectService } from "../../../shared/services/cached-project.service";
import { SliceType } from "../../../models/sliceType";
import { ModelType } from "../../../models/modelType";
import { CachedPaletteService } from "../../../projects/services/cached-palette.service";
import { AgentViz, ShotLocations } from "../xwai-viz.types";
import { allSliceProcessings } from "../../../projects/project/model/slice_processings";
import { ImageFlipperService, ShotOverlayPoint } from "../../../copilot/image-flipper.service";
import { mergeShotOverlay, shotFitColorScale } from "../shot-overlay.utils";
import { SharedModule } from "../../../shared/shared.module";
import { XwaiSliceViewerComponent } from "../xwai-slice-viewer/xwai-slice-viewer.component";

const VP_REF_MODELS = ['Total_Vp_Update']
/** Marks a model type whose values are symmetric about zero (min = -max). */
const ZERO_CENTRED = "_zero_centred_params_process";

/** Resolved slice payload, ready to hand to the {@link XwaiSliceViewerComponent}. */
interface RenderedSlice {
  sliceImage: string;
  sliceArray: number[][];
  colorbar: string;
  limits: { min: number; max: number };
  xLabel: string;
  yLabel: string;
  flippedYAxis: boolean;
}

/**
 * Renders a "model" visualization: a velocity/property model slice with model
 * type, colormap, and iteration controls. Driven entirely by the agent's
 * `viz_params`, so no extra job lookup is needed beyond fetching the slice.
 */
@Component({
  selector: "app-xwai-model-viz",
  templateUrl: "./xwai-model-viz.component.html",
  styleUrls: ["./xwai-model-viz.component.less"],
  imports: [SharedModule, XwaiSliceViewerComponent],
})
export class XwaiModelVizComponent {
  private readonly projectService = inject(ProjectService);
  private readonly cachedProjectService = inject(CachedProjectService);
  private readonly imageLoader = inject(ImageLoaderService);
  private readonly imageFlipper = inject(ImageFlipperService);
  private readonly cachedPaletteService = inject(CachedPaletteService);

  /** Colormap names for the picker, fetched (and cached) from the palette service. */
  protected readonly availableColormaps = rxResource({
    stream: () => this.cachedPaletteService.getPalettes(),
  });

  readonly viz = input.required<AgentViz>();

  readonly job = computed(() => this.viz().job);

  protected image_plot_url: string | undefined;
  protected image_object_url: string | undefined;

  private readonly SLICE_DIM_LABELS: Record<number, string> = {
    [SliceType.inline]: "inline",
    [SliceType.crossLine]: "crossline",
    [SliceType.depth]: "depth",
  };

  protected modelType = linkedSignal(
    () => this.viz().viz_params.model_type ?? "—",
  );
  protected cmap = linkedSignal(() => this.viz().viz_params.cmap ?? "—");
  protected iteration = linkedSignal(() => this.viz().viz_params.iteration);
  protected dx = computed(() => this.viz().job.model_grid.dx ?? 1);

  /** Commit the iteration only when the slider handle is released (not mid-drag). */
  protected onIterationRelease(value: number | number[]) {
    this.iteration.set(Array.isArray(value) ? value[0] : value);
  }

  protected vMin = linkedSignal(() => this.viz().viz_params.min ?? null)
  protected vMax = linkedSignal(() => this.viz().viz_params.max ?? null)
  protected vpRef = linkedSignal<number | null>(
    () => this.viz().viz_params.vp_ref ?? null,
  );
  protected isDepthSlice = computed(() => this.viz().viz_params.slice_dim === 3)
  protected isVpRefModel = computed(() => this.modelType() in VP_REF_MODELS)
  // Seeded from viz_params.show_shots (depth slices may request shots up front),
  // then user-toggleable via the switch.
  protected showShotOverlay = linkedSignal(() => this.viz().viz_params.show_shots ?? false)
  // Editable colour-mapping range for the shot overlay (by traces_fit, 0–100).
  protected shotColorMin = signal<number>(0)
  protected shotColorMax = signal<number>(100)

  /**
   * Whether the selected model type is symmetric about zero. Such models expose
   * a single limit: the user sets the magnitude and min = -max. Falls back to a
   * separate min/max when the type isn't zero-centred (or is unknown).
   */
  protected isZeroCentred = computed(() =>
    (
      (allSliceProcessings as Record<string, string[]>)[this.modelType()] ?? []
    ).includes(ZERO_CENTRED),
  );

  /**
   * Draft values bound to the deferred inputs. Editing these does NOT re-fetch —
   * only {@link applyChanges} commits them into {@link vMin} / {@link vMax} /
   * {@link vpRef}, which the slice resource depends on. Seeded from (and reset
   * by) the applied values. `?? null` preserves 0 as an explicit value.
   */
  protected draftMin = linkedSignal(() => this.vMin());
  protected draftMax = linkedSignal(() => this.vMax());
  /** Single symmetric limit for zero-centred models; seeded from the max. */
  protected draftRange = linkedSignal(() => this.vMax());
  protected draftVpRef = linkedSignal(() => this.vpRef());

  /** True when a draft differs from what's currently applied/rendered. */
  protected pendingChanges = computed(() => {
    const limitsChanged = this.isZeroCentred()
      ? this.draftRange() !== this.vMax()
      : this.draftMin() !== this.vMin() || this.draftMax() !== this.vMax();
    return limitsChanged || this.draftVpRef() !== this.vpRef();
  });

  /** Commit the draft controls, triggering a single re-fetch. */
  protected applyChanges(): void {
    if (this.isZeroCentred()) {
      const range = this.draftRange();
      this.vMax.set(range);
      this.vMin.set(range == null ? null : -range);
    } else {
      this.vMin.set(this.draftMin());
      this.vMax.set(this.draftMax());
    }
    this.vpRef.set(this.draftVpRef());
  }

  /**
   * The min/max actually applied to the rendered slice. When {@link vMin} /
   * {@link vMax} are left empty (null) the backend falls back to the model's
   * global limits, which come back on the result — we surface those as the
   * input placeholders so the user can see the effective range.
   */
  protected effectiveLimits = computed(
    () => this.sliceResource.value()?.limits ?? null,
  );

  private modelTypeParams = computed(() => {
    return {
      id: this.job().id,
      iterations: this.viz().viz_params.iteration,
    };
  });

  protected readonly modelTypesAvailable = rxResource<
    ModelType[],
    { id: string; iterations: number }
  >({
    params: () => this.modelTypeParams(),
    stream: ({ params }) => {
      return (
        this.cachedProjectService.getModelTypes(params.id, params.iterations) ??
        of([])
      );
    },
  });

  /**
   * Fetch the model image. The agent supplies everything `getSlice` needs —
   * model type, colormap, iteration, the slice to cut, and the grid's 2D/3D
   * flag — so we call the plot endpoint directly with no job lookup. We then
   * resolve the rendered PNG to base64 and decode the binary slice array,
   * producing a {@link RenderedSlice} for the viewer.
   */
  protected readonly sliceResource = rxResource<RenderedSlice, any>({
    params: () => {
      const v = this.viz();
      if (!v || !v.job) return undefined;
      const p = v.viz_params;
      return {
        jobId: v.job.id,
        modelType: this.modelType(),
        cmap: this.cmap(),
        iteration: this.iteration(),
        sliceDim: p.slice_dim,
        sliceNumber: p.slice_number,
        grid: v.job.model_grid,
        is2d: v.job.model_grid.is_2d ?? false,
        x2IsInline: v.job.x2_is_inline ?? v.job.model_grid["x2_is_inline"] ?? false,
        v_min: this.vMin(),
        v_max: this.vMax(),
        vp_ref: this.vpRef(),
      };
    },
    stream: ({ params }) =>
      this.projectService
        .getSlice(
          params.jobId,
          params.modelType,
          params.iteration,
          params.sliceDim,
          params.sliceNumber,
          params.is2d,
          params.cmap,
          params.v_min,
          params.v_max,
          undefined, // shotNumber
          undefined, // fwd
          params.vp_ref,
        )
        .pipe(
          switchMap((res) => {
            const { url, colorBarUrl, sliceUrl, min, max, trueMin, trueMax } =
              res;
            const { width, height, xLabel, yLabel, flippedYAxis } =
              this.sliceLayout(params.sliceDim, params.grid, params.x2IsInline);

            return forkJoin({
              sliceImage: this.imageLoader.getImageBase64(url),
              colorbar: of(colorBarUrl),
              sliceArray: this.projectService.getSliceArray(
                sliceUrl,
                width,
                height,
                trueMin,
                trueMax,
              ),
              limits: of({ min, max }),
              xLabel: of(xLabel),
              yLabel: of(yLabel),
              flippedYAxis: of(flippedYAxis),
            });
          }),
          catchError((err) => {
            throw Error(
              err?.error?.detail ||
                err?.message ||
                "An unexpected error occurred",
            );
          }),
        ),
  });

  /**
   * Maps a slice dimension + model grid to the array dimensions and axis labels
   * the viewer needs.
   *
   * `slice_dim` is the grid axis being stepped through, whose inline/crossline
   * meaning depends on `x2_is_inline` (mirrors the model-visualizer):
   *   - default (`x2_is_inline` false): axis 1 = crossline, axis 2 = inline
   *   - `x2_is_inline` true:            axis 1 = inline,    axis 2 = crossline
   * An inline section spans the crossline × depth plane (x = crossline), a
   * crossline section spans inline × depth (x = inline), and a depth section is
   * a crossline × inline map view.
   */
  private sliceLayout(
    sliceDim: number,
    grid: AgentViz["job"]["model_grid"],
    x2IsInline: boolean,
  ): {
    width: number;
    height: number;
    xLabel: string;
    yLabel: string;
    flippedYAxis: boolean;
  } {
    const inlineSize = x2IsInline ? grid.nx2 : grid.nx1;
    const crosslineSize = x2IsInline ? grid.nx1 : grid.nx2;
    const inlineDim = x2IsInline ? SliceType.inline : SliceType.crossLine;
    const crosslineDim = x2IsInline ? SliceType.crossLine : SliceType.inline;

    switch (sliceDim) {
      case inlineDim: // inline section: crossline × depth plane
        return {
          width: crosslineSize,
          height: grid.nx3,
          xLabel: "Crossline",
          yLabel: "Depth",
          flippedYAxis: true,
        };
      case SliceType.depth: // depth section: crossline × inline map view
        return {
          width: crosslineSize,
          height: inlineSize,
          xLabel: "Crossline",
          yLabel: "Inline",
          flippedYAxis: false,
        };
      case crosslineDim: // crossline section: inline × depth plane
      default:
        return {
          width: inlineSize,
          height: grid.nx3,
          xLabel: "Inline",
          yLabel: "Depth",
          flippedYAxis: true,
        };
    }
  }

  /** Maps a {@link SliceType} value to the label used by the image flipper. */

  protected add_image_to_flipper(): void {
    const sliceImage = this.sliceResource.value()?.sliceImage;
    if (!sliceImage) return;

    this.imageFlipper.add_image(
      {
        image_link: sliceImage,
        image_information: {
          job_name: this.job().job_name,
          iteration: this.iteration(),
          slice_number: String(this.viz().viz_params.slice_number),
          dimension:
            this.SLICE_DIM_LABELS[
              this.viz().viz_params.slice_dim
            ],
        },
      },
      this.shotOverlayPoints(),
    );
  }

  /**
   * Snapshots the shot overlay (if currently shown on a depth slice) as points
   * normalized to the slice image (0–1, left→right, top→bottom) — mirrors the
   * positioning math in {@link XwaiSliceViewerComponent.positionShots}, since the
   * flipper only ever gets the flat PNG, not the live D3 overlay.
   */
  private shotOverlayPoints(): ShotOverlayPoint[] | undefined {
    if (!this.isDepthSlice() || !this.showShotOverlay()) return undefined;

    const fits = this.shotsOverlayResource.value();
    const locations = this.shotLocsResource.value();
    const sliceArray = this.sliceResource.value()?.sliceArray;
    if (!fits?.length || !sliceArray?.length) return undefined;

    const dx = this.dx();
    const gridWidth = sliceArray.length;
    const gridHeight = sliceArray[0].length;
    const color = shotFitColorScale(this.shotColorMin(), this.shotColorMax());

    return mergeShotOverlay(fits, locations).map((shot) => ({
      x: shot.sx / dx / gridWidth,
      y: 1 - shot.sy / dx / gridHeight,
      color: color(shot.traces_fit),
      shot_id: shot.shot_id,
      traces_fit: shot.traces_fit,
      sx: shot.sx,
      sy: shot.sy,
      sz: shot.sz,
    }));
  }

  private shotOverlayParams = computed(() => {
    if (this.isDepthSlice()) {
      if (this.showShotOverlay()) return {id: this.job().id, iteration: this.iteration()}
      else return null
    }
  })
  protected readonly shotsOverlayResource = rxResource({
    params: this.shotOverlayParams,
    // defaultValue keeps the resource value ShotOverlayDto[] (never undefined),
    // even during the transient loading state.
    defaultValue: [] as ShotOverlayDto[],
    stream: ({params}) => {
      if (!params) return of<ShotOverlayDto[]>([])
        // we pass params null specifically when it should not be shown, for which case we will set the shots array as []
      return this.cachedProjectService.getShotOverlay(this.job().id, params.iteration)
    }
  })
  
  private shotLocParams = computed(() => {
    if (this.isDepthSlice()) {
      if (this.showShotOverlay()) return {id: this.job().id, iterations_complete: this.job().iterations_completed}
      else return null
    }
  })
  // Shot source locations keyed by shot number, merged with the overlay fits to
  // place shots on a depth slice. defaultValue keeps the value ShotLocations
  // (never undefined), even during the transient loading state.
  protected readonly shotLocsResource = rxResource({
    params: this.shotLocParams,
    defaultValue: {} as ShotLocations,
    stream: ({params}) => {
      // params is null when the overlay should not be shown → empty dict.
      if (!params) return of<ShotLocations>({})

      return this.cachedProjectService
        .getCosts(params.id, params.iterations_complete)
        .pipe(
          map((data) =>
            data?.costItems?.reduce<ShotLocations>((acc, shot) => {
              acc[shot.shotNo] = { sx: shot.sx, sy: shot.sy, sz: shot.sz };
              return acc;
            }, {}) ?? {},
          ),
        )
    }
  })
}
